import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { EMPTY, Observable, combineLatest, of } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  delay,
  map,
  mapTo,
  mergeMap,
  retryWhen,
  switchMap,
  switchMapTo,
  take,
} from 'rxjs/operators';
import { ChangeSoldToActionTypes } from '../../../../features/sold-to-base/store/actions/sold-to-change.action';
import { UserIdService } from '../../../auth';
import { LoginLogoutActionTypes } from '../../../auth/user-auth/store/actions/login-logout.action';
import { GlobalMessageType, isErrorGloballyHandled } from '../../../global-message';
import { GlobalMessageActions } from '../../../global-message/store/actions';
import { ErrorModel, User } from '../../../model';
import { LanguageService, SiteCountryService } from '../../../site-context';
import { SiteContextActions } from '../../../site-context/store';
import { CurrenciesActionTypes } from '../../../site-context/store/actions/currencies.action';
import { LanguagesActionTypes } from '../../../site-context/store/actions/languages.action';
import { normalizeHttpError, withdrawOn } from '../../../util';
import { AppActions } from '../../../util/store/actions';
import { UserProfileConnector } from '../../connectors/user-profile.connector';
import { UserActions } from '../actions/index';
import { UserDetailsActionTypes, UserDetailsActions } from '../actions/user-details.action';
import { UserModuleLoadedActions } from '../actions/user.actions';

@Injectable()
export class UserDetailsEffects {
  private contextChange$ = this.actions$.pipe(ofType(LoginLogoutActionTypes.Logout));

  loadUserDetails$: Observable<UserDetailsActions> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActionTypes.LoadUserDetails),
      map((action: UserActions.LoadUserDetails) => action.payload),
      debounceTime(10),
      mergeMap((userId) => {
        return this.userConnector.load(userId).pipe(
          map((user: User) => new UserActions.LoadUserDetailsSuccess(user)),
          retryWhen((errors) => errors.pipe(delay(2000), take(1))),
          catchError((error) => of(new UserActions.LoadUserDetailsFail(normalizeHttpError(error))))
        );
      }),
      withdrawOn(this.contextChange$)
    )
  );

  loadUserDetailsAfterContextChange$: Observable<UserActions.LoadUserDetails> = createEffect(() =>
    this.actions$.pipe(
      ofType(
        LoginLogoutActionTypes.Login,
        ChangeSoldToActionTypes.ChangeSoldToSuccess,
        UserModuleLoadedActions.USER_MODULE_LOADED,
        LanguagesActionTypes.LanguageChange,
        CurrenciesActionTypes.CurrencyChange
      ),
      switchMapTo(
        this.userIdService.takeUserId(true).pipe(
          map((userId) => new UserActions.LoadUserDetails(userId)),
          catchError(() => EMPTY)
        )
      )
    )
  );

  updateUserDetails$: Observable<UserActions.UpdateUserDetailsSuccess | UserActions.UpdateUserDetailsFail> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActionTypes.UpdateUserDetails),
      map((action: UserActions.UpdateUserDetails) => action.payload),
      concatMap((payload) =>
        this.userConnector.update(payload.username, payload.userDetails).pipe(
          map(() => new UserActions.UpdateUserDetailsSuccess(payload.userDetails)),
          catchError((error) => of(new UserActions.UpdateUserDetailsFail(normalizeHttpError(error))))
        )
      ),
      withdrawOn(this.contextChange$)
    )
  );

  handleSuccessForUpdateUserDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.UserDetailsActionTypes.UpdateUserDetailsSuccess),
      mapTo(
        new GlobalMessageActions.AddMessage({
          text: { key: 'settings.profile.updateSuccess_message' },
          type: GlobalMessageType.MSG_TYPE_CONFIRMATION,
        })
      )
    )
  );

  handleFailureForUpdateUserDetails$: Observable<GlobalMessageActions.AddMessage | AppActions.NoOpAction> = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.UserDetailsActionTypes.UpdateUserDetailsFail),
      map((action: UserActions.UpdateUserDetailsFail) => action.payload),
      switchMap((payload) => {
        if (!isErrorGloballyHandled(payload?.status)) {
          if (Array.isArray(payload?.details)) {
            const messageActionsArray: GlobalMessageActions.AddMessage[] = payload.details
              .filter((errorDetail: ErrorModel) => errorDetail.message)
              .map((errorDetail: ErrorModel) => {
                return new GlobalMessageActions.AddMessage({
                  text: { raw: errorDetail.message },
                  type: GlobalMessageType.MSG_TYPE_ERROR,
                });
              });

            if (messageActionsArray.length) {
              return messageActionsArray;
            }
          }
          return [
            new GlobalMessageActions.AddMessage({
              text: { key: 'settings.profile.updateFailure_message' },
              type: GlobalMessageType.MSG_TYPE_ERROR,
            }),
          ];
        } else {
          return [new AppActions.NoOpAction()];
        }
      })
    )
  );

  changeCustomerSessionLanguage$: Observable<
    SiteContextActions.SetActiveLanguage | SiteContextActions.LoadSiteCountry | GlobalMessageActions.AddMessage
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActionTypes.ChangeCustomerSessionLanguage),
      map((action: UserActions.ChangeCustomerSessionLanguage) => action.payload),
      concatMap((payload) =>
        this.userConnector.changeCustomerSessionLanguage(payload.userId, payload.languageCode).pipe(
          mergeMap(() => this.siteCountryService.getActiveCountry().pipe(take(1))),
          mergeMap((activeCountry) => {
            return [
              new SiteContextActions.SetActiveLanguage(payload.languageCode),
              new SiteContextActions.LoadSiteCountry(payload.userId, activeCountry.isocode),
            ];
          }),
          catchError((_) =>
            of(
              new GlobalMessageActions.AddMessage({
                text: { key: 'errors.changeCustomerSessionLanguageFailure_message' },
                type: GlobalMessageType.MSG_TYPE_ERROR,
              })
            )
          )
        )
      )
    )
  );

  onLoadUserDetailsSuccess$: Observable<
    AppActions.NoOpAction | SiteContextActions.SetActiveLanguage | SiteContextActions.LoadSiteCountry
  > = createEffect(() =>
    this.actions$.pipe(
      ofType(UserDetailsActionTypes.LoadUserDetailsSuccess),
      map((action: UserActions.LoadUserDetailsSuccess) => action.payload),
      // There is a small chance that not all translation chunks will be loaded on time.
      // That's why we add a slight delay before the user default language reload.
      delay(1000),
      mergeMap((user) =>
        combineLatest([
          of(user),
          this.languageService.getActive().pipe(take(1)),
          this.siteCountryService.getActiveCountry().pipe(take(1)),
        ])
      ),
      mergeMap(([user, activeLanguageCode, activeCountry]) => {
        if (user.language.isocode === activeLanguageCode) {
          return [new AppActions.NoOpAction()];
        } else {
          return [
            new SiteContextActions.SetActiveLanguage(user.language.isocode),
            new SiteContextActions.LoadSiteCountry(user.uid, activeCountry.isocode),
          ];
        }
      })
    )
  );

  constructor(
    private actions$: Actions,
    private userConnector: UserProfileConnector,
    private userIdService: UserIdService,
    private languageService: LanguageService,
    private siteCountryService: SiteCountryService
  ) {}
}
